import React, { useEffect } from 'react'
import { Box, Paper, SxProps } from '@mui/material'
import { Sync } from '@mui/icons-material'

import { TabsIcon } from '@/theme/tabs.theme'
import { TabIcon } from '@/theme/tab.theme'

export type CustomTab = {
  id: number
  title: string
  label: string
  titleIcon?: JSX.Element
  childrenComponent: React.ReactNode
  disabled?: boolean
  hide?: boolean
  refreshFunction?: () => void
}

type TabPanelProps = {
  children?: React.ReactNode
  index: any
  value: any
  color?: string
  noPadding?: boolean
  disabled?: boolean
  sx?: SxProps
}

export const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, color, noPadding, disabled, sx, ...other } =
    props

  return (
    <Paper
      elevation={0}
      square
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      className={`${color} ${disabled ? 'disabledDiv' : ''}`}
      sx={{ ...sx }}
      {...other}
    >
      {value === index && <Box py={noPadding ? 0 : 2}>{children}</Box>}
    </Paper>
  )
}

type Props = {
  tabRef?: React.MutableRefObject<HTMLDivElement | null>
  tabsArray: CustomTab[]
  refresh?: boolean
  activeTab?: number
  sxPanel?: SxProps
  sxTab?: SxProps
}

const CustomTabs = ({
  tabsArray,
  tabRef,
  refresh = false,
  activeTab = 0,
  sxPanel,
  sxTab,
}: Props) => {
  const [currentTab, setCurrentTab] = React.useState(activeTab)

  const handleTabChange = (newValue: number) => {
    setCurrentTab(newValue)
  }

  useEffect(() => {
    setCurrentTab(activeTab)
  }, [activeTab])

  useEffect(() => {
    if (tabsArray.length === 1) {
      handleTabChange(0)
    }
  }, [tabsArray])

  return (
    <Box p={1}>
      <TabsIcon
        variant="scrollable"
        value={currentTab}
        onChange={(_, newValue: number) => handleTabChange(newValue)}
        sx={{ ...sxTab }}
      >
        {tabsArray
          .filter((filterItem) => !filterItem.hide)
          .map((tabItem) => (
            <TabIcon
              iconPosition="start"
              ref={tabItem.label === 'alerts' ? tabRef : null}
              key={tabItem.id}
              label={
                <>
                  {tabItem.title}
                  {refresh && currentTab === tabItem.id && (
                    <Box
                      onClick={tabItem.refreshFunction}
                      className="refresh"
                      height={20}
                      pl={1}
                    >
                      <Sync className="green small" />
                    </Box>
                  )}
                </>
              }
              aria-label={tabItem.label}
              icon={tabItem.titleIcon}
              disabled={tabItem.disabled}
            />
          ))}
      </TabsIcon>
      {tabsArray.map((tabItem, index) => (
        <TabPanel
          key={tabItem.id}
          value={currentTab}
          index={index}
          disabled={tabItem.disabled}
          sx={{ ...sxPanel }}
        >
          {tabItem.childrenComponent}
        </TabPanel>
      ))}
    </Box>
  )
}

export default CustomTabs
